import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { twMerge as twm } from 'tailwind-merge'

import { Item } from '../../models/Item'
import { Lesson } from '../../models/Lesson'
import { Option } from '../../models/Option'
import { RecordFactory } from '../../models/RecordFactory'
import { Transaction } from '../../models/Transaction'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { generateRandomID } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import InfoBanner from '../app/design/InfoBanner'
import Paragraph from '../app/design/Paragraph'
import { CalendarColorSelect } from '../calendar/CalendarColorSelect'
import { ContractSearchableSelect } from './ContractSearchableSelect'
import { EmployeeSearchableSelect } from '../contacts/EmployeeSearchableSelect'
import { TypeSearchableSelect } from './TypeSearchableSelect'
import { ImageView } from '../common/ImageView'
import { ItemLocationChips } from './ItemLocationChips'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { Checkbox } from '../common/fields/Checkbox'
import { CurrencyField } from '../common/fields/CurrencyField'
import { Select } from '../common/fields/Select'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { SelectedPhoto } from '../workflows/PhotoPicker'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { EditLessonScheduleModal } from './EditLessonScheduleModal'
import { ItemLocationModal } from './ItemLocationModal'

type Props = {
    item: Item
    videoConferenceOptions: Option[]
    onClose: () => void
    onDelete?: () => void
    isLoading?: boolean
    enableAllFields?: boolean
    onSave: (item: Item) => void
}

const CLASS_COUNT = 30

export const NEW_LESSON_ID_PREFIX = '_new_'

const classTypeOptions = Item.classTypeStrings()

const capacityOptions = Item.capacityStrings(1, Lesson.CLASS_CAPACITY)

const classCountOptions = Item.capacityStrings(1, CLASS_COUNT)

function LessonsDescription({
    lessons,
    onClick,
}: {
    lessons: Lesson[]
    onClick: (lesson: Lesson) => void
}) {
    if (lessons.length === 0) {
        return null
    }

    return (
        <div className="flex flex-col gap-y-2">
            {lessons.map((lesson) => (
                <WorkflowPage.SelectedItem
                    key={lesson.id}
                    title={lesson.date?.format('ddd MMM D, h:mm a')}
                    subtitle={lesson.schedule ? `${lesson.schedule.description}` : 'Single lesson'}
                    thirdLine={
                        lesson.employee ? (
                            <div className="flex items-center gap-x-2">
                                <ImageView
                                    image={lesson.employee?.image}
                                    className="size-6 rounded-full border-2 border-white"
                                    fallbackIcon={{
                                        icon: 'employeeCard.small',
                                        background: 'round',
                                        className: 'border-2 border-white size-6',
                                    }}
                                />
                                <Paragraph size="sm" className="font-normal text-gray-600">
                                    {lesson.employee?.name}
                                </Paragraph>
                            </div>
                        ) : undefined
                    }
                    primaryButtonTitle="Edit"
                    onClickPrimaryButton={() => onClick(lesson)}
                />
            ))}
        </div>
    )
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    const { item } = base.props

    const [lessons] = await Promise.all([RecordFactory.getInstance().loadClasses()])

    return {
        lessons: lessons.filter((l) => l.item?.id === item.id),
    }
}

const render = ({
    data,
    base: {
        owner,
        props: {
            item,
            onSave,
            onClose,
            onDelete,
            enableAllFields,
            isLoading,
            videoConferenceOptions,
        },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const navigate = useNavigate()

    const [scratchClass, setScratchClass] = useState<Item>(item)
    const [lessons, setLessons] = useState<Lesson[]>(data.lessons)

    const [guardrail, setGuardrail] = useState<
        undefined | 'singleCapacity' | 'availability' | 'location' | 'price'
    >(undefined)
    const [selectedPhoto, setSelectedPhoto] = useState<SelectedPhoto | null>(
        item.image?.uri ? { type: 'url', url: item.image.url ?? '' } : null
    )

    const [isSaving, setIsSaving] = useState(false)
    const [addLessonModal, setAddLessonModal] = useState<Lesson | undefined>(undefined)

    const isDropin = scratchClass.classType === 'dropin'
    const isOpen = scratchClass.classType === 'open'

    const zoomOption = videoConferenceOptions.find((o) => o.id === 'zoom')

    const showLessonDetails = Boolean(enableAllFields) && Boolean(scratchClass.id)

    const isSaveEnabled = useMemo(() => {
        return scratchClass.name && scratchClass.classType && scratchClass.capacity
    }, [scratchClass])

    const hasChanges = useMemo(() => {
        return (
            scratchClass.name !== item.name ||
            scratchClass.classType !== item.classType ||
            scratchClass.capacity !== item.capacity ||
            scratchClass.employee !== item.employee ||
            scratchClass.allowConference !== item.allowConference ||
            scratchClass.allowWaitlist !== item.allowWaitlist ||
            scratchClass.isPublic !== item.isPublic ||
            scratchClass.allowSalesTax !== item.allowSalesTax ||
            scratchClass.discount !== item.discount ||
            scratchClass.leadtime !== item.leadtime ||
            scratchClass.isPublic !== item.isPublic ||
            scratchClass.contract !== item.contract ||
            scratchClass.form !== item.form ||
            scratchClass.color !== item.color ||
            scratchClass.memo !== item.memo
        )
    }, [scratchClass, item])

    return (
        <WorkflowPage
            title="Class"
            footerContent={
                <div
                    className={twm(
                        'flex w-full items-center gap-x-4',
                        onDelete !== undefined ? 'justify-between' : 'justify-end'
                    )}
                >
                    {onDelete !== undefined && (
                        <Button
                            intent="secondary"
                            className="text-danger-400"
                            onClick={onDelete}
                            isLoading={isLoading}
                        >
                            Delete class
                        </Button>
                    )}

                    <Button
                        intent="primary"
                        onClick={async () => {
                            setIsSaving(true)

                            await Promise.all(
                                lessons.map((lesson) => {
                                    const clonedLesson = lesson.clone({
                                        item: scratchClass,
                                        capacity: scratchClass.capacity,
                                    })

                                    // if the lesson is new, remove the id so that the backend knows to create it
                                    // the temporary id is used so that we can display and edit the lesson before it's created
                                    if (clonedLesson.id?.startsWith(NEW_LESSON_ID_PREFIX)) {
                                        clonedLesson.id = undefined
                                    }
                                    return RecordFactory.getInstance().sendClass(
                                        clonedLesson,
                                        clonedLesson.schedule,
                                        scratchClass,
                                        false,
                                        false
                                    )
                                })
                            )

                            scratchClass.lessons = lessons
                            onSave(scratchClass)
                        }}
                        disabled={isLoading || isSaving || !isSaveEnabled}
                        isLoading={isSaving}
                    >
                        Save
                    </Button>
                </div>
            }
            onClose={onClose}
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="General">
                    {enableAllFields && (
                        <WorkflowPage.PhotoPicker
                            onSelectPhoto={(photoFile) => {
                                setSelectedPhoto({ type: 'file', file: photoFile })
                            }}
                            onClearPhoto={() => setSelectedPhoto(null)}
                            selectedPhoto={selectedPhoto}
                        />
                    )}
                    <WorkflowPage.IconSection
                        title="Name (required)"
                        icon={<Icon icon="student.large" background="round" />}
                    >
                        <TextField
                            isFullWidth
                            size="medium"
                            placeholder="Enter name"
                            value={scratchClass?.name}
                            onClear={() => setScratchClass((i) => i?.clone({ name: '' }))}
                            onChange={(e) => {
                                setScratchClass((i) => i?.clone({ name: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                    <WorkflowPage.IconSection
                        title="Price"
                        icon={<Icon icon="dollar9.large" background="round" />}
                    >
                        <CurrencyField
                            size="medium"
                            placeholder="0.00"
                            value={scratchClass?.rate}
                            min={0}
                            step={0.01}
                            icon={'dollar9.large'}
                            onClear={() => setScratchClass((i) => i?.clone({ rate: undefined }))}
                            onChange={(e) => {
                                setScratchClass((i) => i?.clone({ rate: e }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Deposit"
                        icon={
                            <Icon
                                icon="dollar3.large"
                                background="round"
                                className={clsx({
                                    'cursor-not-allowed opacity-40': !isDropin,
                                })}
                            />
                        }
                        disabled={!isDropin}
                    >
                        <CurrencyField
                            isFullWidth
                            size="medium"
                            placeholder="0.00"
                            value={scratchClass?.deposit}
                            min={0}
                            step={0.01}
                            icon={'dollar9.large'}
                            onChange={(e) => {
                                setScratchClass((i) => i?.clone({ deposit: e }))
                            }}
                            className={clsx({
                                'cursor-not-allowed opacity-40': !isDropin,
                            })}
                            disabled={!isDropin}
                        />

                        {!isDropin && (
                            <InfoBanner
                                subtitle="Deposits are only available for drop-in classes"
                                intent="info"
                            />
                        )}
                    </WorkflowPage.IconSection>

                    {owner.allowDiscount && (
                        <WorkflowPage.IconSection
                            title="Discount rate"
                            icon={<Icon icon="discount.large" background="round" />}
                        >
                            <Select
                                size="medium"
                                options={Transaction.discountRateStrings().map((option) => ({
                                    id: option.id ?? '',
                                    title: option.label ?? '',
                                }))}
                                selectedOptionID={scratchClass?.discount?.toString()}
                                onSelectOption={(option) => {
                                    if (option) {
                                        setScratchClass((i) => i?.clone({ discount: option.id }))
                                    }
                                }}
                            />
                        </WorkflowPage.IconSection>
                    )}
                    {owner.allowSalesTax && (
                        <WorkflowPage.IconSection
                            title="Allow sales tax"
                            description="Automatically calculate & apply sales tax"
                            hasHoverEffect
                            icon={<Icon icon="percentage2.large" background="round" />}
                            secondaryView={
                                <Checkbox
                                    isChecked={scratchClass.allowSalesTax}
                                    onChange={(e) =>
                                        setScratchClass((i) => i?.clone({ allow_salestax: e }))
                                    }
                                />
                            }
                        />
                    )}
                    <WorkflowPage.IconSection
                        title="Description"
                        icon={<Icon icon="note.large" background="round" />}
                    >
                        <TextArea
                            size="medium"
                            placeholder="Enter description"
                            value={scratchClass?.memo}
                            onClear={() => setScratchClass((i) => i?.clone({ memo: '' }))}
                            onChange={(e) => {
                                setScratchClass((i) => i?.clone({ memo: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    {enableAllFields && (
                        <WorkflowPage.IconSection
                            title="Calendar color"
                            description="Color to be associated with this service on your calendar"
                            icon={<Icon icon="palette.large" background="round" />}
                        >
                            <CalendarColorSelect
                                type="class"
                                color={scratchClass?.color ?? ''}
                                onSelect={(color) => {
                                    setScratchClass((i) => i?.clone({ color }))
                                }}
                            />
                        </WorkflowPage.IconSection>
                    )}
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Details">
                    <WorkflowPage.IconSection
                        title="Class type (required)"
                        icon={<Icon icon="userPlus.large" background="round" />}
                    >
                        <Select
                            size="medium"
                            options={classTypeOptions.map((option) => ({
                                id: option.id ?? '',
                                title: option.label ?? '',
                                subtitle: option.subtitle ?? '',
                            }))}
                            selectedOptionID={scratchClass?.classType?.toString()}
                            onSelectOption={(option) => {
                                setScratchClass((i) =>
                                    i?.clone({
                                        class_type: option.id,
                                        class_count:
                                            option.id !== 'open' ? undefined : i.classCount || 1,
                                    })
                                )
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Class size (required)"
                        icon={<Icon icon="users.large" background="round" />}
                    >
                        <Select
                            size="medium"
                            options={capacityOptions.map((option) => ({
                                id: option.id ?? '',
                                title: option.label ?? '',
                            }))}
                            selectedOptionID={scratchClass?.capacity?.toString()}
                            onSelectOption={(option) => {
                                setScratchClass((i) => i?.clone({ capacity: option.id }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Instructor"
                        icon={<Icon icon="employeeCard.large" background="round" />}
                    >
                        <EmployeeSearchableSelect
                            selectedEmployee={scratchClass.employee}
                            onSelectEmployee={(employee) => {
                                setScratchClass((i) => i?.clone({ employee }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    {isDropin && (
                        <WorkflowPage.IconSection
                            title="Allow waitlist"
                            icon={<Icon icon="clipboard.large" background="round" />}
                            description="Automatically manage your waitlist"
                            hasHoverEffect
                            secondaryView={
                                <Checkbox
                                    disabled={!isDropin}
                                    isChecked={scratchClass?.allowWaitlist}
                                    onChange={(e) =>
                                        setScratchClass((i) => i?.clone({ allow_waitlist: e }))
                                    }
                                />
                            }
                        />
                    )}

                    {Boolean(owner.plan) && (
                        <WorkflowPage.IconSection
                            title="Zoom link"
                            icon={<Icon icon="video.large" background="round" />}
                            description="Attach a Zoom link"
                            hasHoverEffect
                            secondaryView={
                                <Checkbox
                                    isChecked={scratchClass?.allowConference}
                                    disabled={!zoomOption?.enabled}
                                    onChange={(e) =>
                                        setScratchClass((i) =>
                                            i?.clone({
                                                allow_conference: e,
                                                conference_type: e ? 'zoom' : undefined,
                                            })
                                        )
                                    }
                                />
                            }
                        >
                            {!zoomOption?.enabled && (
                                <InfoBanner
                                    subtitle="This feature has not been enabled for your account. It can be enabled within settings"
                                    intent="info"
                                    buttonText="Go to settings"
                                    onClick={() => {
                                        navigate('/settings/videoconferencing')
                                    }}
                                />
                            )}
                        </WorkflowPage.IconSection>
                    )}

                    {isOpen && (
                        <WorkflowPage.IconSection
                            title={`Number of classes${isOpen ? ' (required)' : ''}`}
                            icon={<Icon icon="calendars.large" background="round" />}
                        >
                            <Select
                                size="medium"
                                options={classCountOptions.map((option) => ({
                                    id: option.id ?? '',
                                    title: option.label ?? '',
                                }))}
                                selectedOptionID={scratchClass?.classCount?.toString()}
                                isDisabled={!isOpen}
                                onSelectOption={(option) => {
                                    setScratchClass((i) => i?.clone({ class_count: option.id }))
                                }}
                            />
                        </WorkflowPage.IconSection>
                    )}
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Scheduling">
                    <WorkflowPage.IconSection
                        title="Schedule"
                        icon="calendar.large"
                        hasHoverEffect={lessons.length === 0}
                        hasReducedHoverEffect={lessons.length > 0}
                        description="Setup a schedule for your class"
                        secondaryView={
                            showLessonDetails ? (
                                <Button
                                    intent="tertiaryLink"
                                    className="p-0"
                                    href={`/settings/classes/${scratchClass.id}/lessons`}
                                >
                                    View & edit lessons
                                </Button>
                            ) : (
                                <Button
                                    intent="tertiaryLink"
                                    className="p-0"
                                    onClick={() =>
                                        setAddLessonModal(
                                            new Lesson({
                                                id: `${NEW_LESSON_ID_PREFIX}${generateRandomID()}`,
                                                capacity: scratchClass.capacity,
                                                employee: scratchClass.employee,
                                            })
                                        )
                                    }
                                >
                                    Add lesson
                                </Button>
                            )
                        }
                    >
                        {!showLessonDetails && (
                            <LessonsDescription
                                lessons={lessons}
                                onClick={(lesson) => setAddLessonModal(lesson)}
                            />
                        )}
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Lead time"
                        icon={<Icon icon="stopwatch.large" background="round" />}
                    >
                        <Select
                            size="medium"
                            selectedOptionID={scratchClass?.leadtime}
                            options={Item.leadtimeStrings(scratchClass.type).map((option) => ({
                                id: option.id ?? '',
                                title: option.label ?? '',
                            }))}
                            onSelectOption={(option) => {
                                setScratchClass((i) => i?.clone({ leadtime: option.id }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    {owner.allowWidget && (
                        <WorkflowPage.IconSection
                            title="Show online"
                            description="Allow clients to purchase this service online"
                            icon={<Icon icon="computerClock.large" background="round" />}
                            hasHoverEffect
                            secondaryView={
                                <Checkbox
                                    isChecked={scratchClass?.isPublic}
                                    onChange={(e) => {
                                        setScratchClass((i) => i?.clone({ is_public: e }))
                                    }}
                                />
                            }
                        />
                    )}
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Attachments">
                    <WorkflowPage.IconSection
                        title="Attach a contract"
                        description="This contract will be signed when a client purchases this service"
                        icon={<Icon icon="pen.large" background="round" />}
                    >
                        <ContractSearchableSelect
                            selectedContract={scratchClass.contract}
                            onSelectContract={(c) => {
                                setScratchClass((i) => i?.clone({ contract: c }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Attach a form"
                        description="This form will be completed when a client purchases this service"
                        icon={<Icon icon="field.large" background="round" />}
                    >
                        <TypeSearchableSelect
                            owner={owner}
                            selectedForm={scratchClass.form}
                            onSelectForm={(f) => {
                                setScratchClass((i) => i?.clone({ form: f }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Location">
                    <WorkflowPage.IconSection
                        title="Location"
                        icon={<Icon icon="markerPin.large" background="round" />}
                    >
                        <ItemLocationChips item={scratchClass} onUpdateItem={setScratchClass} />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <ConfirmationModal
                    title="Would you like to continue without adding a price?"
                    isOpen={guardrail === 'price'}
                    onClose={() => setGuardrail(undefined)}
                    onConfirm={() => {
                        setScratchClass((i) => i?.clone({ rate: 0 }))
                        setGuardrail(undefined)
                    }}
                >
                    <Paragraph className="text-gray-600">
                        If you don’t add a price this service will be shown as a price of “TBD” and
                        allow you to edit the price on a service by service basis.
                    </Paragraph>
                    <Paragraph className="mt-4 text-gray-600">
                        This is great if you don’t necessarily know the price of the service before
                        booking it.
                    </Paragraph>
                </ConfirmationModal>
                <ItemLocationModal
                    item={scratchClass}
                    isOpen={guardrail === 'location'}
                    onClose={() => {
                        setGuardrail(undefined)
                    }}
                    onConfirm={(item) => {
                        setScratchClass(item)
                        setGuardrail(undefined)
                    }}
                />

                <ConfirmationModal
                    title="Single capacity"
                    isOpen={guardrail === 'singleCapacity'}
                    onClose={() => setGuardrail(undefined)}
                    onConfirm={() => {
                        setScratchClass((i) => i?.clone({ capacity: 1 }))
                        setGuardrail(undefined)
                    }}
                >
                    <Paragraph className="text-gray-600">
                        By toggling single capacity on this service can only be booked once.
                        Multiple clients will not be able to book this service in the same time
                        slot.
                    </Paragraph>
                </ConfirmationModal>

                {addLessonModal && (
                    <EditLessonScheduleModal
                        isOpen
                        onClose={() => setAddLessonModal(undefined)}
                        allowRepeatForever={scratchClass.classType !== 'series'}
                        lesson={addLessonModal}
                        onDone={(editedLesson) => {
                            setAddLessonModal(undefined)
                            setLessons((lessons) => {
                                if (!lessons.find((l) => l.id === editedLesson.id)) {
                                    return [...lessons, editedLesson]
                                }

                                return lessons.map((l) => {
                                    if (l.id === editedLesson.id) {
                                        return editedLesson.clone()
                                    }

                                    return l
                                })
                            })
                        }}
                    />
                )}
            </div>
            <NavigationBlockerPrompt
                when={hasChanges && !isSaving}
                allowCurrentPaths={['/settings/classes/:id/lessons']}
                allowNextPaths={['/settings/classes/:id/lessons']}
            />
        </WorkflowPage>
    )
}

export const ClassPage = usePSComponent(render, viewWillAppear)
